'use client'

import * as React from 'react'
import { LazyMotion, domAnimation, m, useInView, useReducedMotion } from 'framer-motion'
import { ImgWithFallback } from '@/components/ui/img-with-fallback'
import { EASE } from '@/lib/motion'

// The AI Tagging hero frame: a real UGC-style photo with the tagging
// pipeline choreographed over it. When it scrolls into view a scan line
// sweeps the frame, the primary bounding box draws in with its label,
// and the detection chips settle in underneath, like watching the
// two-pass vision run. Plays once; reduced motion shows the final state.
//
// The photo is the repo-verified "tailored coat" shot from the fashion
// pool, so the labels (trench coat / waist belt) describe what is
// actually in the image. Positions are intentionally loose (a large
// centre box + corner chips) so the overlay never visibly misaligns.

const PHOTO =
  'https://images.unsplash.com/photo-1551489186-cf8726f514f8?auto=format&fit=crop&w=900&h=900&q=80'

const DETECTIONS = [
  { label: 'Trench coat · 94', state: 'Auto-accepted', good: true },
  { label: 'Waist belt · 72', state: 'Queued for review', good: false },
]

export function AiTaggingFrame() {
  const ref = React.useRef<HTMLDivElement>(null)
  const inView = useInView(ref, { once: true, margin: '0px 0px -15% 0px' })
  const reduce = useReducedMotion()
  const play = inView && !reduce
  const done = inView // reduced motion: jump straight to the final state

  return (
    <LazyMotion features={domAnimation}>
      <div ref={ref} className="relative aspect-square overflow-hidden rounded-2xl border bg-secondary">
        <ImgWithFallback
          src={PHOTO}
          alt="UGC fashion photo being auto-tagged"
          fill
          sizes="(min-width: 1024px) 32vw, 90vw"
          className="object-cover"
        />

        {/* Frame chrome */}
        <span className="absolute left-3 top-3 z-20 rounded bg-foreground/85 px-2 py-0.5 text-[10px] font-bold text-background backdrop-blur">
          FRAME · 0:08
        </span>
        <span className="absolute right-3 top-3 z-20 rounded bg-card/90 px-2 py-0.5 text-[10px] font-medium text-foreground backdrop-blur">
          UGC reel · @priya
        </span>

        {/* Scan sweep: a thin lime line + soft wash passing down the frame once. */}
        {play && (
          <m.div
            aria-hidden
            className="pointer-events-none absolute inset-x-0 z-10 h-16"
            style={{
              background:
                'linear-gradient(to bottom, transparent, hsl(74 100% 66% / 0.22) 70%, hsl(74 100% 66% / 0.9) 99%, transparent)',
            }}
            initial={{ top: '-18%' }}
            animate={{ top: '108%' }}
            transition={{ duration: 1.1, ease: 'linear', delay: 0.2 }}
          />
        )}

        {/* Primary bounding box: draws in after the scan passes its area. */}
        <m.div
          aria-hidden
          className="absolute left-[14%] right-[14%] top-[18%] bottom-[26%] z-10 rounded-lg border-2 border-sky-400/90 shadow-[0_0_0_9999px_rgba(13,20,36,0.10)]"
          initial={reduce ? false : { opacity: 0, scale: 1.06 }}
          animate={done ? { opacity: 1, scale: 1 } : undefined}
          transition={{ duration: 0.4, ease: EASE, delay: reduce ? 0 : 0.9 }}
        >
          <m.span
            className="absolute -top-3 left-2 rounded bg-sky-500 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white shadow"
            initial={reduce ? false : { opacity: 0, y: 4 }}
            animate={done ? { opacity: 1, y: 0 } : undefined}
            transition={{ duration: 0.3, ease: EASE, delay: reduce ? 0 : 1.05 }}
          >
            Trench coat · 94
          </m.span>
          {/* The hotspot pin the widget will place, gently pulsing once tagged. */}
          <span className="absolute bottom-[24%] left-1/2 z-10 flex h-5 w-5 -translate-x-1/2 items-center justify-center">
            <span className="absolute h-5 w-5 rounded-full bg-white/60 motion-safe:animate-ping" />
            <span className="relative h-3 w-3 rounded-full border-2 border-white bg-sky-500 shadow" />
          </span>
        </m.div>

        {/* Detection chips: the pipeline's verdicts settle in bottom-left. */}
        <div className="absolute bottom-3 left-3 right-3 z-20 flex flex-col gap-1.5">
          {DETECTIONS.map((d, i) => (
            <m.span
              key={d.label}
              className="inline-flex w-fit items-center gap-1.5 rounded-full bg-card/95 px-2.5 py-1 text-[10px] font-semibold text-foreground shadow backdrop-blur"
              initial={reduce ? false : { opacity: 0, x: -10 }}
              animate={done ? { opacity: 1, x: 0 } : undefined}
              transition={{ duration: 0.35, ease: EASE, delay: reduce ? 0 : 1.2 + i * 0.18 }}
            >
              <span
                aria-hidden
                className={`h-1.5 w-1.5 rounded-full ${d.good ? 'bg-emerald-500' : 'bg-amber-500'}`}
              />
              {d.label}
              <span className="font-normal text-muted-foreground">· {d.state}</span>
            </m.span>
          ))}
        </div>
      </div>
    </LazyMotion>
  )
}
